<template>
  <FullPageLayout>
    <template v-if="fetchState.pending">
      <OLoading class="u-center my-32" />
      <div></div>
    </template>
    <template v-else-if="fetchState.error">
      <ErrorPageLayout :error="fetchState.error" />
    </template>
    <template v-else>
      <div class="mx-auto w-full sm:w-[1100px]">
        <div class="pl-4 sm:pl-0">
          <OBreadcrumb class="capitalize text-md leading-18 mt-6 mb-4" :items="breadcrumbRoutes" />
          <div class="flex items-center mb-6">
            <h2 class="font-bold text-22 sm:text-24 leading-32">Sana Özel Seçtiklerimiz</h2>
            <div class="border-t border-[#E2E5ED] flex-grow sm:mr-5 ml-2"></div>
          </div>
        </div>
        <ArticleSlider
          v-if="sliderArticles.length"
          class="px-4 sm:px-0 mb-6"
          :articles="sliderArticles"
          arrow-color="#8197af"
          :data-layer="{ name: `Sanaozel - Slider` }"
        >
          <SwiperSlide v-for="(article, index) in sliderArticles" :key="article._id">
            <CarouselArticle :article="article" variant="tag" :lazy="index !== 0" />
          </SwiperSlide>
        </ArticleSlider>
        <div class="w-full max-w-[1100px] flex flex-wrap items-stretch mx-auto sm:-ml-2 space-y-4 sm:space-y-0 mt-4 sm:mt-0">
          <ListArticleCard
            v-for="(item, position) in recommendedArticles"
            :key="item._id"
            :article="item"
            :data-layer="{ name: `Sanaozel - Liste`, position: position + 1 }"
          />
        </div>
        <OLoading v-if="hasNextPage" ref="infiniteScrollRef" class="mt-6 u-center" />
      </div>
    </template>
  </FullPageLayout>
</template>

<script>
import { defineComponent, onMounted, ref, useContext, useFetch, useMeta, useRoute } from '@nuxtjs/composition-api';
import FullPageLayout from '@/modules/common/components/layout/FullPageLayout';
import ErrorPageLayout from '@/modules/common/components/layout/ErrorPageLayout.vue';
import { OBreadcrumb, OLoading } from '@/modules/common/components/ui';
import SwiperSlide from '@/modules/sliders/core/SwiperSlide';
import ListArticleCard from '~/modules/card/templates/common/ListArticleCard';
import ArticleSlider from '~/modules/sliders/components/common/ArticleSlider';
import CarouselArticle from '~/modules/card/templates/common/CarouselArticle';
import { useTracking } from '~/modules/tracking/composables/useTracking';
import { useAuth } from '~/modules/auth/state/useAuth';
import { useInfiniteScroll } from '~/composables/useInfiniteScroll';

export default defineComponent({
  head: {
    bodyAttrs: {
      class: 'bg-[#f7f8fb] o-ps-global',
    },
  },
  components: {
    FullPageLayout,
    ErrorPageLayout,
    OLoading,
    OBreadcrumb,
    ListArticleCard,
    ArticleSlider,
    CarouselArticle,
    SwiperSlide,
  },
  setup() {
    useMeta(() => ({
      meta: [{ hid: 'robots', name: 'robots', content: 'noindex' }],
    }));
    // ------- Google Pageview Event Beginning -------
    const { sendPageviewEvent } = useTracking();
    sendPageviewEvent({
      page: {
        type: 'Kategori Liste',
      },
    });
    // ------- Google Pageview Event End -------
    const { $service, $error } = useContext();
    const route = useRoute();
    const breadcrumbRoutes = [{ href: route.value.fullPath, name: 'Sana Özel Seçtiklerimiz' }];
    const { user, isLoggedIn } = useAuth();
    const page = ref(0);
    const hasNextPage = ref(false);
    const recommendedArticles = ref([]);
    const sliderArticles = ref([]);
    const categories = route.value.query.category;
    const scopeId = route.value.query.scopeId;

    const { fetch, fetchState } = useFetch(async () => {
      try {
        if (process.client) {
          page.value++;
          const OOetTi = JSON.parse(localStorage.getItem('o-oet-ti'))?.data;
          const userId = isLoggedIn.value ? user.value._id : OOetTi;
          const { data, pagination } = await $service.category.getRecommendedArticles({
            userId,
            categories,
            scopeId,
            placementId: 2,
            page: page.value,
          });
          if (sliderArticles.value.length <= 0) {
            sliderArticles.value = data.slice(0, 9);
          }
          if (page.value > 1) {
            recommendedArticles.value = recommendedArticles.value.concat(data);
          }
          hasNextPage.value = pagination.hasNextPage;
        }
      } catch (error) {
        $error(error);
      }
    });

    const [infiniteScrollRef] = useInfiniteScroll(
      async () => {
        await fetch();
      },
      { rootMargin: '300px' }
    );

    onMounted(async () => {
      await fetch();
      await fetch();
    });

    return { fetchState, breadcrumbRoutes, recommendedArticles, sliderArticles, infiniteScrollRef, hasNextPage };
  },
});
</script>
